import {gsap} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {BruteForceSymbol, BruteForceSymbolStates} from "../../../game/reels/BruteForceSymbol";
import {BruteForceXNudgeSymbol} from "../../../game/reels/BruteForceXNudgeSymbol";
import {BruteForceBlankSymbol} from "../../../game/reels/BruteForceBlankSymbol";
import {BruteForceReelView} from "../../../game/reels/BruteForceReelView";
import {BruteForce} from "../../../game/BruteForce";
import {BruteForceScatterSymbol} from "../../../game/reels/BruteForceScatterSymbol";
import {CustomEase} from "gsap/dist/CustomEase";


export interface Positionable {
    position: {
        x: number,
        y: number
    }
}

export interface SimpleReelModelSymbolPosition {
    reelID: number;
    position: number
}


export interface SimpleReelModelSymbolData {
    name: string,
    positionable: Positionable,
    reelPosition: number,
    symHeight: number
}

export enum SimpleReelModelState {
    IDLE = "IDLE",
    START = "START",
    SPIN = "SPIN",
    STOP = "STOP",
    STOPPED = "STOPPED",
    BOUNCE = "BOUNCE",
    NUDGE = "NUDGE",
}

export interface SimpleReelModelEventData {
    reel: number;
    state: SimpleReelModelState;
}


export interface SimpleReelPaddingData {
    visualPaddingBefore: number;
    paddingBefore: number;
    visibleSymbols: number;
    paddingAfter: number;
    visualPaddingAfter: number;
}

export class SimpleReelModel {
    public paddingConfig: SimpleReelPaddingData = {
        visualPaddingBefore: 3,
        paddingBefore: 0,
        visibleSymbols: 5,
        paddingAfter: 0,
        visualPaddingAfter: 3,
    }
    public animatedStepData = {
        y: 0
    }
    public stepSize: number = 96;
    public readonly id: number;
    /**
     * Triggers on: ReelStateChange and each ReelModelState
     *
     *
     */
    public readonly events: APIEventSystem | undefined;
    private _reelSetIndex: number = 0;
    private _stopIndex: number;
    private _stickySymbol: BruteForceSymbol[] = [];
    private _spinCount: number = 0;
    private _spinning: boolean = false;
    private _currentSpinStep: gsap.core.Tween | undefined;
    private animationTimeScale = 1;
    private stepDuration = 0.045;
    private stopSymbolDuration: number = 0.03;
    private _stopping: boolean = false;
    private triggerStop: boolean = false;
    private _startedReverse: boolean;
    private masterSpinTimeline: gsap.core.Timeline | undefined;
    private _nudgeSteps: number = 0;

    constructor(id: number, triggerEvents: boolean = false) {
        this.id = id;
        this._reelView = new BruteForceReelView(this.id);
        if (triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
            this.events.on("ReelStateChange", (data: SimpleReelModelEventData) => this.onReelStateChange(data)); //To prevent event system from saving once callbacks if no on callbacks is set.
        }

    }

    private _stopTriggerTl: gsap.core.Timeline | undefined;

    get stopTriggerTl(): gsap.core.Timeline | undefined {
        return this._stopTriggerTl;
    }

    //Fake reel
    private _reelSet: string[] | undefined;

    get reelSet(): string[] {
        if (!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.id}`);
        }
        return this._reelSet;
    }

    private _stopSymbols: SimpleReelModelSymbolData[] = [];

    get stopSymbols(): SimpleReelModelSymbolData[] {
        return this._stopSymbols;
    }

    private _symbols: SimpleReelModelSymbolData[] = [];

    get symbols(): SimpleReelModelSymbolData[] {
        return this._symbols;
    }

    private _stopTimeline: gsap.core.Timeline | undefined;

    get stopTimeline(): gsap.core.Timeline | undefined {
        return this._stopTimeline;
    }

    private _speed: number = this.animationTimeScale / this.stepDuration;

    get speed(): number {
        return this._speed;
    }

    set speed(value: number) {
        this._speed = value;
        this.animationTimeScale = value * this.stepDuration;
        this.masterSpinTimeline?.timeScale(this.animationTimeScale);
    }

    private _state: SimpleReelModelState = SimpleReelModelState.IDLE;

    get state(): SimpleReelModelState {
        return this._state;
    }

    set state(value: SimpleReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <SimpleReelModelEventData>{reel: this.id, state: this._state});
        }
    }

    private _reelView: BruteForceReelView;

    public get reelView(): BruteForceReelView {
        return this._reelView;
    }

    public get stepsToNudge(): number {
        return this._nudgeSteps;
    }

    public set stepsToNudge(value: number) {
        this._nudgeSteps = value;
    }

    set stepDurationValue(value: number) {
        this.stopSymbolDuration = value;
    }

    public setReelSet(reelSet: string[], index?: number) {
        this._reelSet = reelSet;
        index = index != undefined ? index : Math.floor(Math.random() * this._reelSet.length);
        this.setReelIndex(index);
    }

    public init(symbols: string[]) {
        this.clearSymbols(this._symbols);
        this.clearSymbols(this._stopSymbols);

        const totalCount = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;
        const totalInitCount = this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter;

        if (symbols.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.id}`);
        }

        symbols = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(symbols);
        symbols = symbols.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));

        for (let i: number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(symbols[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.IDLE);
            this._symbols.push(symbol);
            this._stopSymbols.push(symbol);
        }

        this.updatePosition();
    }

    public halt() {
        if (this._currentSpinStep) {
            this.masterSpinTimeline?.kill();
            this._currentSpinStep?.progress(1);
        }
    }

    public resume() {
        this.startSpin();
    }

    public stop(stopSymbolNames: string[], reverse: boolean = false): gsap.core.Timeline {
        reverse = this._startedReverse;

        this.clearSymbols(this._stopSymbols);

        this._stopSymbols = [];

        for (let i: number = 0; i < stopSymbolNames.length; i++) {
            const symbol = this.createNewSymbol(stopSymbolNames[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.STOP);
            symbol.positionable.position.y = this.stepSize * -(this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols);
            this._stopSymbols.push(symbol);
        }

        this._stopTriggerTl = gsap.timeline({
            paused: true,
            onStart: () => {
                this.state = SimpleReelModelState.STOP;
                this._spinning = false;
                this.triggerStop = true;
            }
        });

        this._stopTimeline = gsap.timeline({
            paused: true,
            onStart: () => {
                this._stopIndex = stopSymbolNames.length - 1;
                this._stopping = true;
            },
            onComplete: () => {
                this.state = SimpleReelModelState.STOPPED;
                this._stopTimeline = undefined;
                this._stopping = false;
                this.state = SimpleReelModelState.IDLE;
            }
        });

        const numberOfNormalSteps = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;
        for (let i: number = 0; i < numberOfNormalSteps; i++) {
            const tween = this.step(reverse);
            this._stopTimeline.add(tween);
        }

        this._stopTimeline.add(this.bounce());
        this._stopTimeline.add(() => {
            this.stickySymbolMoveBacktoSymbols()
        }, this._stopTimeline.totalDuration());

        this._stopTriggerTl.add(gsap.to(this, this._stopTimeline.totalDuration(), {}));

        return <gsap.core.Timeline>this._stopTriggerTl.play(0);
    }

    public start(reverse: boolean = false): gsap.core.Timeline {
        this._startedReverse = reverse;
        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: () => {
                this.state = SimpleReelModelState.START;
                this._stopSymbols = [];
            },
            onComplete: () => {
                this.startSpin(reverse);
            }
        });

        const tween = this.step(reverse);
        tween.vars.ease = "back.in(3)";
        tween.totalDuration(this.stepDuration * 4);

        tl.add(tween);

        return tl;
    }

    public bounce(bounceFactor: number = 0.5, durationFactor: number = 1, reverse: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const overshoot: gsap.core.Tween = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * (durationFactor / 2),
            y: reverse ? -this.stepSize * bounceFactor : this.stepSize * bounceFactor,
            ease: "power2.out",
            onStart: () => {
                this.state = SimpleReelModelState.BOUNCE;
            },
            onUpdate: () => this.updatePosition(),
        });

        const back: gsap.core.Tween = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * durationFactor * 2,
            y: 0,
            ease: "power2.out",
            onUpdate: () => this.updatePosition(),
        });

        tl.add(overshoot);
        tl.add(back);

        return tl;
    }

    public nudge(reverse: boolean = false, ispartOfWin: boolean): gsap.core.Tween {
        const tween: gsap.core.Tween = this.nudgeStep(reverse, ispartOfWin);
        tween.eventCallback("onStart", () => {
            this._nudgeSteps += reverse ? 1 : -1;
            this.state = SimpleReelModelState.NUDGE;
            this.onAnimationStart(tween, reverse);
        });

        return tween;
    }

    public getAllVisibleStopSymbols(): SimpleReelModelSymbolData [] {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this._nudgeSteps;
        return this._stopSymbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
    }

    public getStopSymbolAt(symbolIndex: number) {
        return this._symbols[symbolIndex + this.paddingConfig.visualPaddingBefore].positionable;
    }

    public getVisibleSymbolAt(row: number) {
        const visibleSymbols = this.getAllVisibleStopSymbols();
        return <BruteForceSymbol>visibleSymbols[row].positionable;
    }

    public setInteractiveSymbols(bool: boolean): void {
        const mode: string = BruteForce.model.data.mode;
        const nextMode: string = BruteForce.model.nextMode;

        // if any of the mode is FS/SFS
        if (mode.indexOf("FREESPIN") > -1 && nextMode.indexOf("FREESPIN") > -1) {
            return;
        }

        for (let ctr: number = 0; ctr < this._stopSymbols.length; ctr++) {
            const sym: SimpleReelModelSymbolData = this._stopSymbols[ctr],
                reelPosition: number = sym.reelPosition;

            const symbol: BruteForceSymbol = (sym.positionable as BruteForceSymbol);
            if ((reelPosition > -1 && reelPosition < 5)) {
                symbol.setInteractive(bool);
            }
        }
    }

    public updateNudgeSymPosInArray(): void {
        const currentSyms: SimpleReelModelSymbolData[] = this.symbols;
        const nudgeSym = currentSyms.find((item: SimpleReelModelSymbolData) => item.name === "XN");
        //console.log(nudgeSym);
        const nudgeIndex: number = currentSyms.indexOf(nudgeSym!);
        currentSyms.splice(nudgeIndex, 1);
        currentSyms.splice(3, 0, nudgeSym!);
        // console.log(currentSyms);
        this.symbols.forEach((item: SimpleReelModelSymbolData, index: number) => {
            item.reelPosition = index - 3;
        });
        for (let i: number = 4; i < 8; i++) {
            (<BruteForceSymbol>this.symbols[i].positionable).visible = false;
        }
        this.updatePosition();
    }

    public hideVisualSymbol(): void {
        const currentSyms: SimpleReelModelSymbolData[] = this.symbols;
        if (!this._symbols[3].name.includes("XN")) {
            (<BruteForceSymbol>this.symbols[3].positionable).visible = false;
        }
        if (!this._symbols[7].name.includes("XN")) {
            (<BruteForceSymbol>this.symbols[7].positionable).visible = false;
        }
    }

    public stickySymbolLayerUp(XNSym: BruteForceXNudgeSymbol): void {
        if (this.reelView.isReelContainingFullNudge) {

            const currentSyms: SimpleReelModelSymbolData[] = this.symbols;
            //console.log(nudgeSym);
            //nudge sym is 3rd element in symbols array, reelposition 0
            const sym: SimpleReelModelSymbolData = {
                name: "B",
                positionable: this.createSymbol("B", SimpleReelModelState.SPIN, {reelID: this.id, position: 0}),
                reelPosition: 0,
                symHeight: 1
            }
            currentSyms.splice(3, 1);
            currentSyms.splice(3, 0, sym);
            // console.log(currentSyms);

            this.updatePosition();
            this._stickySymbol = [];
            this._stickySymbol.push(XNSym);
            this.reelView.stickyContainer.addChild(XNSym);
            this.reelView.addChild(this.reelView.stickyContainer);
            if (BruteForce.allreelsView.reelModels.every((reelModel: SimpleReelModel) => reelModel.reelView.isReelContainingFullNudge)) {
                this._reelView.stickyContainer.alpha = 0.75;
            }
        }
    }

    public updateBetlineSymbolPosition(reelId: number): void {

        this._symbols.forEach((symbol) => {
            const mySym: BruteForceSymbol = <BruteForceSymbol>symbol.positionable;
            if (symbol.name === "XN") {
                this.reelView.addChild(this.reelView.nudgeContainer.addChild(mySym));
            } else if (symbol.name === "SC") {
                this.reelView.addChild(this.reelView.scatterContainer.addChild(<BruteForceXNudgeSymbol>symbol.positionable));
            } else {
                this.reelView.addChild(<BruteForceSymbol>symbol.positionable);
            }
            mySym.position.x = 0;
        });
        this.reelView.addChild(this.reelView.nudgeContainer);
        this.reelView.addChild(this.reelView.scatterContainer)
        this.reelView.addChild(this.reelView.stickyContainer);
        this.updatePosition();
    }

    private onReelStateChange(data: SimpleReelModelEventData): void {
        this.events?.trigger(data.state, data);
    }

    private getNextSymbolName(nextSymbolPosition: number): string {
        let nextIndex = this._reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this.reelSet.length + nextIndex;
        }
        if (nextIndex >= this.reelSet.length) {
            nextIndex = nextIndex - this.reelSet.length;
        }
        if (this.state === SimpleReelModelState.NUDGE) {
            return `L${Math.floor(Math.random() * 5) + 1}`
        }

        return this.reelSet[nextIndex];

    }

    private getNextSymbol(position: number, state: SimpleReelModelState): SimpleReelModelSymbolData {
        if (this._stopping && this._stopSymbols.length > 0) {
            const symbol = this._stopSymbols[this._stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }
        //Default
        return this.createNewSymbol(this.getNextSymbolName(position), position, state)
    }

    private createNewSymbol(name: string, position: number, state: SimpleReelModelState): SimpleReelModelSymbolData {
        let symbol: SimpleReelModelSymbolData;
        if (this.reelView.nudgeReelPos === -2) {
            symbol = {
                name: name,
                positionable: this.createSymbol(name, state, {reelID: this.id, position: position}),
                reelPosition: position,
                symHeight: name == "XN" ? 5 : 1
            }
            if (name === "XN") {
                this._reelView.nudgeReelPos = symbol.reelPosition;
            }
        } else {
            if (name === "XN") {
                name = "B";
            }
            symbol = {
                name: name,
                positionable: this.createSymbol(name, state, {reelID: this.id, position: position}),
                reelPosition: position,
                symHeight: name == "XN" ? 5 : 1
            }
        }
        return symbol;
    }

    private getRandomSymbolInReelSet(): string {
        const syms: string[] = ["L1", "M1", "L5", "M3", "L4", "M2", "L3", "M4", "L2", "M5"];
        const randomIndex = Math.floor(Math.random() * syms.length);
        return syms[randomIndex];
    }

    private clearSymbols(symbols: SimpleReelModelSymbolData[]): void {
        let symbol;
        while (symbol = symbols.pop()) {
            this.deleteSymbol(symbol, this.state);
        }
        if (!this._reelView.isReelContainingFullNudge) {
            this._reelView.nudgeReelPos = -2;
        }
    }

    private getRandomSymbols(count: number): string[] {
        const result: string[] = [];
        for (let i: number = 0; i < count; i++) {
            result.push(this.getRandomSymbolInReelSet());
        }
        return result;
    }

    private startSpin(reverse: boolean = false) {
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this.masterSpinTimeline = gsap.timeline();
        this.masterSpinTimeline.add(this.spin(reverse));
    }

    private spin(reverse: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                if (this._spinning) {
                    this.masterSpinTimeline?.add(this.spin(reverse));
                    //this.spin(reverse);
                }
            }
        })
        tl.add(this.step(reverse));
        return tl;
    }

    private step(reverse: boolean = false): gsap.core.Tween {
        const tween: gsap.core.Tween = gsap.to(this.animatedStepData, this.stopSymbolDuration, {
            y: reverse ? -this.stepSize : this.stepSize,
            ease: "none",
            onStart: () => this.onAnimationStart(tween, reverse),
            onUpdate: () => this.updatePosition(),
            onComplete: () => this.onAnimationComplete(reverse)
        });
        return tween;
    }

    private nudgeStep(reverse: boolean = false, isXNPartOfWin: boolean): gsap.core.Tween {
        const tween: gsap.core.Tween = gsap.to(this.animatedStepData, isXNPartOfWin ? 0.25 : 0.03, {
            y: reverse ? -this.stepSize : this.stepSize,
            ease: CustomEase.create("custom", "M0,0 C0,0 0.071,-0.001 0.131,0.073 0.199,0.159 0.243,0.303 0.298,0.466 0.34,0.593 0.402,0.766 0.413,0.799 0.432,0.858 0.503,1.006 0.525,1.04 0.566,1.106 0.613,1.143 0.676,1.144 0.759,1.144 0.845,1.021 0.875,1 0.94,0.953 1,1 1,1 "),
            onUpdate: () => this.updatePosition(),
            onComplete: () => this.onAnimationComplete(reverse)
        });
        return tween;
    }

    private onAnimationStart(tween: gsap.core.Tween, reverse: boolean = false) {
        this._currentSpinStep = tween;
        this.addSymbol(reverse);
    }

    private onAnimationComplete(reverse: boolean = false) {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            symbol.reelPosition += reverse ? -1 : 1;
        }

        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this._stopTimeline?.play(0);
        }

        this.updateIndex(reverse);
        this.removeSymbol();
        this.updatePosition();
    }

    private updateIndex(reverse: boolean) {
        const valueChange = reverse ? 1 : -1
        if (this._stopping) {
            this.setStopIndex(this._stopIndex + valueChange);
        } else {
            this.setReelIndex(this._reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value: number) {
        if (value >= this._stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this._stopSymbols.length - 1;
        }
        this._stopIndex = value;
    }

    private setReelIndex(value: number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this._reelSetIndex = value;
    }

    private addSymbol(reverse: boolean) {
        let newSymbols = [];
        if (reverse) {
            const newSymbolPosition = this._symbols[this._symbols.length - 1].reelPosition + 1;
            newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));
            //newSymbols.push(this.createNewSymbol(this.getNextSymbolName(newSymbolPosition), newSymbolPosition, this.state));
        } else {
            const newSymbolPosition = this._symbols[0].reelPosition - 1;
            newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));
        }


        this._symbols = this._symbols.concat(newSymbols);
        /*  console.log(this._symbols.length);
          console.log(this._symbols);*/
        this._symbols.sort((a: any, b: any) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });

        if (this.id === 4 || this.id === 3) {
            if (BruteForce.model.scatterAnticipationCopy[this.id]) {
                if (BruteForce.allreelsView.reelModels[JSON.parse(JSON.stringify(this.id)) - 1].state !== SimpleReelModelState.SPIN) {
                    this._symbols.forEach((symData: SimpleReelModelSymbolData, index) => {
                        if (symData.name == "SC") {
                            (<BruteForceScatterSymbol>symData.positionable).showTeaseEffect();
                        }
                    });
                }
            }
        }
    }

    private removeSymbol() {
        for (let i: number = this._symbols.length - 1; i >= 0; i--) {
            const symbol = this._symbols[i];
            if (symbol.reelPosition > this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter - 1) {
                //Remove from bottom
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this.deleteSymbol(removedSymbol, this.state);


            }
            if (symbol.reelPosition < -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore) {
                //Remove from top
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this.deleteSymbol(removedSymbol, this.state);

            }
        }
    }

    private updatePosition() {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            const basePos = this.stepSize * symbol.reelPosition;
            symbol.positionable.position.y = basePos + this.animatedStepData.y;
        }
    }

    private createSymbol(symbolName: string, state: SimpleReelModelState, symbolPosition: SimpleReelModelSymbolPosition): Positionable {
        const symbolState: BruteForceSymbolStates = state == SimpleReelModelState.SPIN ? BruteForceSymbolStates.SPIN : BruteForceSymbolStates.NORMAL;
        let symbol: BruteForceSymbol;
        if (symbolName === "XN") {
            symbol = new BruteForceXNudgeSymbol(symbolName, symbolPosition, symbolState);
        } else if (symbolName === "B") {
            symbol = new BruteForceBlankSymbol(symbolName, symbolPosition, symbolState);
        } else if (symbolName === "SC") {
            symbol = new BruteForceScatterSymbol(symbolName, symbolPosition, symbolState);
        } else {
            symbol = new BruteForceSymbol(symbolName, symbolPosition, symbolState);
        }
        symbol.position.x = 0;
        symbol.position.y = symbolPosition.position * this.stepSize * (symbolName.includes("XN") ? 5 : 1);
        symbolName.includes("XN") ? this._reelView.nudgeContainer.addChild(symbol) : symbolName.includes("SC") ? this._reelView.scatterContainer.addChild(symbol) : this._reelView.addChild(symbol);
        this.reelView.addChild(this.reelView.nudgeContainer);
        this.reelView.addChild(this.reelView.scatterContainer)
        this.reelView.addChild(this.reelView.stickyContainer);
        return symbol;
    }

    private deleteSymbol(symbolData: SimpleReelModelSymbolData, state: SimpleReelModelState): void {
        (<BruteForceSymbol>symbolData.positionable).destroy({children: true});
    }

    private stickySymbolMoveBacktoSymbols(): void {
        if (this.reelView.isReelContainingFullNudge) {
            const currentSyms: SimpleReelModelSymbolData[] = this.symbols;
            const currentXNsym: SimpleReelModelSymbolData = {
                name: this._stickySymbol[0].symbolName,
                positionable: this._stickySymbol[0],
                reelPosition: 0,
                symHeight: 5
            }
            currentSyms.splice(3, 1);
            currentSyms.splice(3, 0, currentXNsym);
            //console.log("moving back", currentSyms);

            this.reelView.nudgeContainer.addChild(this._stickySymbol[0]);
        }
    }

}